import { Component, ChangeDetectionStrategy } from '@angular/core';
import { GoApplicationButtonsComponent } from '../go-application-buttons/go-application-buttons.component';
import { takeUntil, filter, tap } from 'rxjs';
import { NavigationText } from '../../../../../../core/constants/navigation-text';
import { ApplicationState } from '../../../../../../core/models/application-state/application-state';
import { NavigationOptions } from '../../../../../../shared/components/page-header-nav/models/navigation-options';
import { PageHeaderNavItem } from '../../../../../../shared/components/page-header-nav/models/page-header-nav-item';
import { StepLink } from '../../../../shared/models/step-link';
import { StepNavigationButtonVisibilityService } from '../../../../shared/services/step-navigation-button-visibility/step-navigation-button-visibility.service';
import { StepNavigationButtonService } from '../../../../shared/services/step-navigation-button/step-navigation-button.service';
import { StepNavigationService } from '../../../../shared/services/step-navigation/step-navigation.service';
import { NavigationLinkDefinitions } from '../../../../shared/constants/navigation-link-definitions';
import { GoStorageService } from '../../../go-parameters/services/go-storage/go-storage.service';
import { GoRoutes } from '../../../../../../core/constants/routes';
import { ApplicationPageFacadeService } from '../../../../direct-entry/shared/services/application-page-facade/application-page-facade.service';
import { GoApplicationPageNavigationBaseComponent } from '../go-application-page-navigation-base/go-application-page-navigation-base.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
    selector: 'app-go-application-page-navigation',
    imports: [GoApplicationButtonsComponent],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './go-application-page-navigation.component.html',
})
export class GoApplicationPageNavigationComponent extends GoApplicationPageNavigationBaseComponent {
    navItems: NavigationOptions = {};
    applicationState: ApplicationState | null = null;

    public title: string | undefined;

    constructor(
        protected override readonly stepNavigationService: StepNavigationService,
        protected override readonly stepNavigationButtonService: StepNavigationButtonService,
        protected override readonly stepNavigationOptionsService: StepNavigationButtonVisibilityService,
        protected override readonly dialog: MatDialog,
        private readonly applicationPageFacadeService: ApplicationPageFacadeService,
        private readonly goStorageService: GoStorageService,
    ) {
        super(
            stepNavigationService,
            stepNavigationButtonService,
            stepNavigationOptionsService,
            dialog,
        );
    }

    clearCacheAndNext() {
        this.goStorageService.clearCacheData();
        this.next();
    }

    clearAllAppDataAndNext() {
        this.applicationPageFacadeService.applicationStateService.clearApplicationState();
        this.clearCacheAndNext();
    }

    protected subscribeToStepChanges(): void {
        this.stepNavigationService.currentStep$
            .pipe(
                takeUntil(this.destroy$),
                filter(
                    (currentStep): currentStep is StepLink =>
                        currentStep !== undefined,
                ),
                tap((currentStep: StepLink) => {
                    this.setNavigationLinks(currentStep);
                }),
            )
            .subscribe();
    }

    private setNavigationLinks(stepLink: StepLink) {
        this.setPrimaryLinks(stepLink);
        this.setSecondaryLinks(stepLink);
    }

    private setSecondaryLinks(stepLink: StepLink) {
        const secondaryLinks: PageHeaderNavItem[] = [];

        if (
            this.stepNavigationOptionsService.needsCallEWAForAssistanceButton(
                stepLink,
            )
        ) {
            secondaryLinks.push(
                this.stepNavigationButtonService.createCustomCssLink(
                    NavigationText.CONTACT_EWA_FOR_ASSISTANCE,
                    NavigationLinkDefinitions.CallEwaForAssistanceLinkCss,
                    this.openContactForAssistanceModal.bind(this),
                    NavigationLinkDefinitions.BluePhoneImageUrl,
                ),
            );
        }

        this.navItems.secondaries = secondaryLinks;
    }

    private setPrimaryLinks(stepLink: StepLink) {
        const primaryLinks: PageHeaderNavItem[] = [];

        if (
            this.stepNavigationOptionsService.needsGoConfirmTechnicalDataButton(
                stepLink,
            )
        ) {
            primaryLinks.push(
                this.stepNavigationButtonService.createPrimaryLink(
                    NavigationText.GO_CONFIRM_TECHNICAL_DATA,
                    this.clearCacheAndNext.bind(this),
                ),
            );
        } else if (
            this.stepNavigationOptionsService.needsNextButton(stepLink)
        ) {
            if (GoRoutes.isPurposeOfFilingRoute(stepLink.url)) {
                primaryLinks.push(
                    this.stepNavigationButtonService.createPrimaryLink(
                        NavigationText.NEXT,
                        this.clearAllAppDataAndNext.bind(this),
                    ),
                );
            } else {
                primaryLinks.push(
                    this.stepNavigationButtonService.createPrimaryLink(
                        NavigationText.NEXT,
                        this.next.bind(this),
                    ),
                );
            }
        }

        this.navItems.primaries = primaryLinks;
    }
}
